import React, { ReactNode, useContext, useMemo } from 'react';
import {
  ConfigContext,
  withLocalization,
  withLocalizationProps,
} from '../../context/Config';
import NewGroupCardDefaultImg from '../../images/newGroupCard.svg';
import Text from '../Text';
import { Avatar, Space, Tag } from 'antd';
import Link from '../Link';
import { randomHex } from '../../utils/helper';
import findLastIndex from 'lodash/findLastIndex';
import withSkeleton from '../../hoc/withSkeleton';
import min from 'lodash/min';
import IconNewUsers from '../Icons/IconNewUsers';
import NewImageGroupCard from '../NewImageGroupCard';
import NewGroupCardSkeleton from './NewGroupCardSkeleton';
import { IconBook, IconCalendarV3 } from '../Icons';
import Wrapper from '../Wrapper';

import './NewGroupCard.less';

export type NewGroupCardType = {
  id?: string;
  name?: string | ReactNode;
  description?: string;
  groupTitle?: string;
  teachers?: Array<{
    fullName: string;
    image: string;
  }>;
  level?: string;
  templateId?: any;
  studentsCount?: number;
  status?: string;
};

export type NewGroupCardProps = {
  group: NewGroupCardType;
  contentHeight?: string;
  image?: string;
  actions?: ReactNode;
  lessonsCount?: {
    totalLessons: number;
    participatedLessons: number;
  };
  scheduleViewer?: ReactNode | null;
  addStudentBtn?: ReactNode | null;
  dotMenu?: ReactNode | null;
  performanceStatus?: ReactNode;
  attendanceStatus?: ReactNode;
  userIconFill?: '';
  levelPlaningStartDate?: any;
  levelPlaningEndDate?: any;
  groupCourseName?: any;
  students?: any;
  showStatus?: boolean;
};

const TNewGroupCard: React.FC<NewGroupCardProps & withLocalizationProps> = ({
  moment,
  group,
  lessonsCount,
  dotMenu,
  performanceStatus,
  attendanceStatus,
  levelPlaningStartDate,
  levelPlaningEndDate,
  groupCourseName,
  students,
  showStatus = true,
}) => {
  const { translate } = useContext(ConfigContext);
  const randomColorHex = useMemo(randomHex, []);

  const { teachers = [] } = group;

  const nameFirstLetters = (name = '') => {
    const arr = name
      .split(' ')
      .map((s) => s.trim())
      .filter(Boolean);

    while (arr.length > 2) {
      const minVal = min(arr.map((s) => s.length));
      arr.splice(
        findLastIndex(arr, (s) => s.length === minVal),
        1,
      );
    }

    return arr.map((s) => s.charAt(0).toLocaleUpperCase()).join('');
  };

  function showTeacher(teachers) {
    if (!teachers || !teachers.length) {
      return null;
    }

    const activeTeacher = teachers.find((teacher) => teacher.active === true);
    const teacher = activeTeacher ? activeTeacher : teachers[0];
    return (
      <div className="t-teacher-info">
        <Avatar
          size="small"
          src={teacher?.image}
          alt={teacher?.fullName}
          icon={nameFirstLetters(teacher?.fullName)}
          style={{ background: randomColorHex, fontSize: 11 }}
        />
        <span className="t-teacher-name">
          <Text level={3} rows={1}>
            {teacher?.fullName}
          </Text>
        </span>
      </div>
    );
  }

  const teacher = showTeacher(teachers);

  function showStudent() {
    if (!students || !students.length) {
      return null;
    }

    if (students.length === 1) {
      return (
        <Space size={4}>
          <span className="t-studentIcon">
            <IconNewUsers />
          </span>
          <Text level={3} rows={1}>
            {students[0]?.studentId?.fullName}
          </Text>
        </Space>
      );
    }

    return (
      <Text level={3} rows={1}>
        {students[0]?.studentId?.fullName || students[0]?.studentId?.image}
      </Text>
    );
  }

  const renderStudentsData = () => {
    return (
      <Space size={4} className="t-studentsData">
        <span className="t-studentIcon">
          <IconNewUsers />
        </span>
        <Text rows={1} level={3}>
          {group?.studentsCount || 0}
        </Text>
        <Text rows={1} level={3}>
          {translate('Students')}
        </Text>
      </Space>
    );
  };

  const renderGroupStatus = (item) => {
    switch ((item.levelPlaningEndDate, item.closed)) {
      case !item.closed && moment().isAfter(item.levelPlaningEndDate):
        return (
          <Tag className="t-tagStyle" color="success">
            {translate('Active')}
          </Tag>
        );
      case !item.closed && moment().isBefore(item.levelPlaningEndDate):
        return (
          <Tag className="t-tagStyle" color="blue">
            {translate('Finished')}
          </Tag>
        );
      case !!item.closed:
        return (
          <Tag className="t-tagStyle" color="error">
            {translate('Closed')}
          </Tag>
        );
      default:
        return (
          <Tag className="t-tagStyle" color="default">
            {translate('Unknown')}
          </Tag>
        );
    }
  };

  const renderGroupDateSection = () => {
    return (
      <Wrapper display="flex">
        <Text
          rows={1}
          level={3}
          textColor="dark-50"
          title={`${levelPlaningStartDate || ''} - ${
            levelPlaningEndDate || ''
          }`}
        >
          <span className="t-iconCalendarStyle">
            <IconCalendarV3 />
          </span>
          <span>{`${levelPlaningStartDate || ''} - ${
            levelPlaningEndDate || ''
          }`}</span>
        </Text>
      </Wrapper>
    );
  };

  const renderLessonsLeft = () => {
    return (
      <Wrapper display="flex">
        <Text
          rows={1}
          level={3}
          textColor="dark-50"
          title={String(
            lessonsCount?.totalLessons - lessonsCount?.participatedLessons || 0,
          )}
        >
          <span className="t-iconBookStyle">
            <IconBook size="xs" />
          </span>
          {lessonsCount?.totalLessons - lessonsCount?.participatedLessons || 0}
          <span className="t-lessonsLeftTitle">
            {translate('Lessons left')}
          </span>
        </Text>
      </Wrapper>
    );
  };

  const renderDotSymbol = () => {
    return (
      <Text rows={1} level={3} textColor="dark-50">
        &#x2022;
      </Text>
    );
  };

  return (
    <NewImageGroupCard
      dotMenu={<div className="t-dot-menu">{dotMenu}</div>}
      image={group?.templateId?.image || NewGroupCardDefaultImg}
      performanceStatus={performanceStatus}
      attendanceStatus={attendanceStatus}
    >
      <div className="t-groupCardContent">
        <div className="t-groupContentRow-1">
          {showStatus && renderGroupStatus(group)}
          {(levelPlaningStartDate || levelPlaningEndDate) && (
            <>
              {lessonsCount && renderDotSymbol()}
              {renderGroupDateSection()}
            </>
          )}
          {lessonsCount && (
            <>
              {renderDotSymbol()}
              {renderLessonsLeft()}
            </>
          )}
        </div>

        <div className="t-groupContentRow-2">
          <div className="t-groupCardTitle">
            <Link
              href={`/group/${group?.id}`}
              title={
                <Text
                  title={String(group.name || '')}
                  textColor="dark-100"
                  semiBold
                  level={1}
                  rows={1}
                >
                  {group.name || ''}
                </Text>
              }
            />
            {groupCourseName || ''}
          </div>
        </div>

        <div className="t-groupContentRow-3">
          <div className="t-teacherAndStudentInfo">
            {/* Teachers info */}
            {!!teachers.length && (
              <div className="t-icon-with-text">
                <div className="t-info-text">
                  <Text rows={1} level={4}>
                    {teacher}
                  </Text>
                </div>
              </div>
            )}

            {/* Students info */}
            {students && students.length === 1
              ? showStudent()
              : renderStudentsData()}
          </div>
        </div>
      </div>
    </NewImageGroupCard>
  );
};

export default withSkeleton(
  withLocalization(TNewGroupCard),
  NewGroupCardSkeleton,
);
